// Program / tenant scope + current updater identity.
//
// `getProgramId()` is resolved PER CALL so SPA navigation between programs
// (e.g. `/team/<a>/...` → `/team/<b>/...`) takes effect without a reload.
// Order: URL path segment after /team/  →  Constants.PROGRAM_ID (env-driven,
// read from RPM_PROGRAM_ID via Vite; throws if unset). No hardcoded id — the
// env value varies per environment (dev/beta vs prod), so a literal fallback
// would silently pick the wrong program.
//
// Call `getProgramId()` inside each service method — never close over it at
// module load (SPA nav between programs must take effect without a reload).

import { Constants } from '@/config/constants';
import { normalizeRoleId, ENGAGE_ROSTER_ROLE, getDashboardTaskRoles } from '@/data/roles';

/** Resolve the current staff role for Tier 4 `updated_by_role` attribution. */
export function currentUpdaterRole(): string {
  return normalizeRoleId(localStorage.getItem('access_user_role')) || 'care_coordinator';
}

/**
 * Resolve the current program id. Called per-request.
 * Order: URL `/team/<programid>/...` → Constants.PROGRAM_ID (env-driven).
 */
export function getProgramId(): string {
  if (typeof window !== 'undefined') {
    const m = window.location.pathname.match(/\/team\/([^/]+)/);
    if (m?.[1]) return m[1];
  }
  return String(Constants.PROGRAM_ID);
}

export interface RosterScope {
  role: string;
  programid: string;
  task_roles?: string[];
}

/** The roster Engage lists and its sidebar badge counts: care_coordinator scope for every role, on the
 *  env program (the `/engage` route carries no `/team/<id>`). One builder, so the two cannot drift. */
export function engageRosterScope(): RosterScope {
  return {
    role: ENGAGE_ROSTER_ROLE,
    programid: String(Constants.PROGRAM_ID),
    task_roles: getDashboardTaskRoles(ENGAGE_ROSTER_ROLE) ?? undefined,
  };
}

/** Snapshot at module load — back-compat for callers that imported the constant.
 *  Prefer `getProgramId()` for live reads. */
export const PROGRAM_ID = getProgramId();
